import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlayIcon, 
  ClockIcon,
  SparklesIcon,
  TvIcon,
  RadioIcon,
  SpeakerWaveIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAudio } from '../contexts/AudioContext';

const Home: React.FC = () => {
  const { startSession } = useAudio();
  const [forYouEpisodes, setForYouEpisodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingSession, setStartingSession] = useState<string | null>(null);

  // Array of gradient backgrounds for episode cards
  const gradientBackgrounds = [
    'bg-gradient-to-br from-purple-500 to-pink-500',
    'bg-gradient-to-br from-blue-500 to-cyan-500',
    'bg-gradient-to-br from-green-500 to-emerald-500',
    'bg-gradient-to-br from-orange-500 to-red-500',
    'bg-gradient-to-br from-indigo-500 to-purple-500',
    'bg-gradient-to-br from-teal-500 to-blue-500',
    'bg-gradient-to-br from-rose-500 to-pink-500',
    'bg-gradient-to-br from-amber-500 to-orange-500',
    'bg-gradient-to-br from-violet-500 to-purple-500',
    'bg-gradient-to-br from-emerald-500 to-teal-500',
    'bg-gradient-to-br from-sky-500 to-blue-500',
    'bg-gradient-to-br from-fuchsia-500 to-pink-500',
    'bg-gradient-to-br from-lime-500 to-green-500',
    'bg-gradient-to-br from-red-500 to-rose-500',
    'bg-gradient-to-br from-cyan-500 to-teal-500',
    'bg-gradient-to-br from-yellow-500 to-amber-500',
    'bg-gradient-to-br from-slate-500 to-gray-500',
    'bg-gradient-to-br from-stone-500 to-neutral-500',
    'bg-gradient-to-br from-zinc-500 to-gray-500',
    'bg-gradient-to-br from-neutral-500 to-slate-500'
  ];

  // Function to get gradient background for an episode based on its index
  const getEpisodeGradient = (index: number) => {
    return gradientBackgrounds[index % gradientBackgrounds.length];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const episodesData = await apiService.getForYouEpisodes(20);
        setForYouEpisodes(episodesData);
      } catch (error) {
        console.error('Error fetching for-you data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleStartSession = async (episodeId: string, episodeTitle: string) => {
    try {
      setStartingSession(episodeId);
      await startSession(episodeId, episodeTitle);
      // Audio will start playing automatically via the floating player
    } catch (error) {
      console.error('Error starting session:', error);
    } finally {
      setStartingSession(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-8">
      {/* For You Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            For You
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Latest episodes from your favorite shows
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/channels"
            className="inline-flex items-center justify-center space-x-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors"
          >
            <TvIcon className="w-4 h-4" />
            <span>All Channels</span>
          </Link>
          <Link
            to="/shows"
            className="inline-flex items-center justify-center space-x-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors"
          >
            <RadioIcon className="w-4 h-4" />
            <span>All Shows</span>
          </Link>
        </div>
      </div>

      {/* For You Episodes */}
      {forYouEpisodes.length > 0 ? (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {forYouEpisodes.map((episode, index) => (
              <div
                key={episode._id}
                className={`${getEpisodeGradient(index)} rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group cursor-pointer relative overflow-hidden`}
                onClick={() => handleStartSession(episode._id, episode.title)}
              >
                {/* Semi-transparent overlay for better text readability */}
                <div className="absolute inset-0 bg-white/10 dark:bg-black/30 rounded-xl"></div>
                
                {/* Content positioned above overlay */}
                <div className="relative z-10 mb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    {episode.channel.logoUrl ? (
                      <img 
                        src={episode.channel.logoUrl} 
                        alt={episode.channel.name}
                        className="w-10 h-10 rounded-lg object-cover ring-2 ring-white/40 dark:ring-white/30"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-white/25 dark:bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center ring-2 ring-white/40 dark:ring-white/30">
                        <TvIcon className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white dark:text-white truncate drop-shadow-sm">
                        {episode.channel.name}
                      </h3>
                      <p className="text-xs text-white/90 dark:text-white/80 truncate drop-shadow-sm">
                        {episode.show.name}
                      </p>
                    </div>
                  </div>
                  
                  <h4 className="text-lg font-semibold text-white dark:text-white mb-2 line-clamp-2 drop-shadow-sm">
                    {episode.title}
                  </h4>
                  
                  {episode.description && (
                    <p className="text-sm text-white/95 dark:text-white/90 line-clamp-3 mb-3 drop-shadow-sm">
                      {episode.description}
                    </p>
                  )}
                </div>
                
                {/* Bottom section positioned above overlay */}
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="w-4 h-4 text-white/90 dark:text-white/80" />
                    <span className="text-xs text-white/90 dark:text-white/80 drop-shadow-sm">
                      {new Date(episode.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartSession(episode._id, episode.title);
                    }}
                    disabled={startingSession === episode._id}
                    className="bg-white/25 dark:bg-white/20 backdrop-blur-sm hover:bg-white/35 dark:hover:bg-white/30 border border-white/40 dark:border-white/30 text-white font-semibold py-2 px-4 rounded-lg flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {startingSession === episode._id ? (
                      <>
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <span className="text-sm">Starting...</span>
                      </>
                    ) : (
                      <>
                        <PlayIcon className="w-4 h-4" />
                        <span className="text-sm">Play</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <SparklesIcon className="w-12 h-12 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            No Episodes Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            No episodes are available yet. Check back later for new content.
          </p>
          <Link
            to="/channels"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
          >
            <TvIcon className="w-5 h-5" />
            <span>Browse Channels</span>
          </Link>
        </div>
      )}

      {/* Marketing Content - Features and Statistics */}
      {forYouEpisodes.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose AI News?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Experience news like never before with our innovative AI-powered discussions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <TvIcon className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Curated Channels</h3>
              <p className="text-gray-600 dark:text-gray-400">Specialized news channels covering different topics and perspectives</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <SpeakerWaveIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Spatial Audio</h3>
              <p className="text-gray-600 dark:text-gray-400">Immersive audio experience that makes you feel part of the conversation</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 dark:bg-pink-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserGroupIcon className="w-8 h-8 text-pink-600 dark:text-pink-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">AI Personalities</h3>
              <p className="text-gray-600 dark:text-gray-400">Each panelist has unique expertise, voice, and discussion style</p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-12 text-center">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto">
              <div>
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                  {forYouEpisodes.length}+
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Episodes</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                  {new Set(forYouEpisodes.map(ep => ep.channel._id)).size}+
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Channels</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-pink-600 dark:text-pink-400 mb-1">
                  {new Set(forYouEpisodes.map(ep => ep.show._id)).size}+
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Shows</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                  24/7
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Available</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;